import React, { FC, useEffect, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { infer as Infer, boolean, date, object, string } from 'zod'
import { Box, FormControlLabel, Grid, Switch, Typography } from '@mui/material'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'

import {
  AppDialog,
  FormInputAutosuggest,
  FormInputDateField,
  FormInputTextField,
  Loader,
} from '../../../../components'
import { showAlertAction } from '../../../../store/alerts'
import { useAppDispatch } from '../../../../hooks'
import { fetchSlipsListAction } from '../../../../store/slips'
import {
  addLeadingZero,
  getFormattedDate,
  pincodeRegex,
} from '../../../../utils'
import { states } from '../../../../utils/appUtils'

import ItemListBox, {
  sumCgstValue,
  sumIgstValue,
  sumSgstValue,
  sumTaxableAmount,
  sumTotalValue,
} from './ItemListBox'
import {
  EInvoiceAddress,
  EInvoiceInfoResponse,
  EInvoiceItem,
  IrnValDetailsResponse,
  generateEInvoice,
  getEInvoiceInfo,
} from './Services'

const supplyTypes = [
  { id: 'B2B', label: 'B2B (Business to Business)' },
  { id: 'SEZWP', label: 'SEZWP (SEZ with Payment)' },
  { id: 'SEZWOP', label: 'SEZWOP (SEZ without Payment)' },
  { id: 'EXPWP', label: 'EXPWP (Export with Payment)' },
  { id: 'EXPWOP', label: 'EXPWOP (Export without Payment)' },
  { id: 'DEXP', label: 'DEXP (Deemed Export)' },
]

const docTypes = [
  { id: 'INV', label: 'INV (Invoice)' },
  { id: 'CRN', label: 'CRN (Credit Note)' },
  { id: 'DBN', label: 'DBN (Debit Note)' },
]

const ADDR_MAX = 100
const PLACE_MAX = 100

const schema = object({
  supplyType: string({ required_error: 'Please select supply type' }),
  docType: string({ required_error: 'Please select document type' }),
  docNo: string({ required_error: 'Please enter document number' })
    .min(1)
    .max(16)
    .regex(
      /^([a-zA-Z1-9]{1}[a-zA-Z0-9\/-]{0,15})$/,
      'Invalid document number format'
    ),
  docDate: date({ required_error: 'Please enter document date' }),
  reverseCharge: boolean().default(false),

  issuerName: string().optional(),
  issuerGSTIN: string().optional(),
  issuerState: string().optional(),

  dispatchFromAddress1: string()
    .min(1, 'Address 1 is required')
    .max(ADDR_MAX, `Max ${ADDR_MAX} chars`),
  dispatchFromAddress2: string()
    .max(ADDR_MAX, `Max ${ADDR_MAX} chars`)
    .optional(),
  dispatchFromPlace: string()
    .min(3, 'Min 3 chars')
    .max(PLACE_MAX, `Max ${PLACE_MAX} chars`),
  dispatchFromPincode: string({ required_error: 'Please enter pincode' }).regex(
    pincodeRegex,
    'Please provide valid pincode'
  ),
  dispatchFromState: string({ required_error: 'Please select state' }),

  partyName: string().optional(),
  partyGSTIN: string().optional(),
  partyState: string().optional(),
  shipToGstin: string().optional(),

  shipToAddress1: string()
    .min(1, 'Address 1 is required')
    .max(ADDR_MAX, `Max ${ADDR_MAX} chars`),
  shipToAddress2: string().max(ADDR_MAX, `Max ${ADDR_MAX} chars`).optional(),
  shipToPlace: string()
    .min(3, 'Min 3 chars')
    .max(PLACE_MAX, `Max ${PLACE_MAX} chars`),
  shipToPincode: string({ required_error: 'Please enter pincode' }).regex(
    pincodeRegex,
    'Please provide valid pincode'
  ),
  shipToState: string({ required_error: 'Please select state' }),
})

type Schema = Infer<typeof schema>

const IssuerDispatchBox = ({
  registerFieldRef,
}: {
  registerFieldRef?: (name: string, el: HTMLDivElement | null) => void
}) => {
  const { control } = useFormContext()
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="h6" sx={{ mt: 4, fontWeight: 'bold' }}>
              Issuer Details
            </Typography>
          </Grid>
          <Grid item>
            <FormInputTextField
              control={control}
              name="issuerName"
              label="Name"
              InputProps={{ readOnly: true }}
              required
            />
          </Grid>
          <Grid item>
            <FormInputTextField
              control={control}
              name="issuerGSTIN"
              label="GSTIN"
              InputProps={{ readOnly: true }}
              required
            />
          </Grid>
          <Grid item>
            <FormInputAutosuggest
              control={control}
              name="issuerState"
              label="Select State"
              readOnly
              options={states.map((s) => ({ label: s.label, id: s.code }))}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="h6" sx={{ mt: 4, fontWeight: 'bold' }}>
              Dispatch From
            </Typography>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: '16px', width: '100%' }}>
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) =>
                  registerFieldRef?.('dispatchFromAddress1', el)
                }
              >
                <FormInputTextField
                  control={control}
                  name="dispatchFromAddress1"
                  label="Address 1"
                  required
                />
              </Box>
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) =>
                  registerFieldRef?.('dispatchFromAddress2', el)
                }
              >
                <FormInputTextField
                  control={control}
                  name="dispatchFromAddress2"
                  label="Address 2"
                />
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: '16px', width: '100%' }}>
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) =>
                  registerFieldRef?.('dispatchFromPlace', el)
                }
              >
                <FormInputTextField
                  control={control}
                  name="dispatchFromPlace"
                  label="Place"
                  required
                  fullWidth
                />
              </Box>
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) =>
                  registerFieldRef?.('dispatchFromPincode', el)
                }
              >
                <FormInputTextField
                  control={control}
                  name="dispatchFromPincode"
                  label="PinCode"
                  required
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              ref={(el: HTMLDivElement | null) =>
                registerFieldRef?.('dispatchFromState', el)
              }
            >
              <FormInputAutosuggest
                control={control}
                name="dispatchFromState"
                label="State"
                readOnly
                options={states.map((s) => ({
                  label: s.label,
                  id: s.code,
                }))}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const PartyShipToBox = ({
  registerFieldRef,
}: {
  registerFieldRef?: (name: string, el: HTMLDivElement | null) => void
}) => {
  const { control } = useFormContext()
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="h6" sx={{ mt: 4, fontWeight: 'bold' }}>
              Party Details
            </Typography>
          </Grid>
          <Grid item>
            <FormInputTextField
              control={control}
              name="partyName"
              label="Name"
              InputProps={{ readOnly: true }}
              required
            />
          </Grid>
          <Grid item>
            <FormInputTextField
              control={control}
              name="partyGSTIN"
              label="GSTIN"
              InputProps={{ readOnly: true }}
              required
            />
          </Grid>
          <Grid item>
            <FormInputAutosuggest
              control={control}
              name="partyState"
              label="Select State"
              readOnly
              options={states.map((s) => ({ label: s.label, id: s.code }))}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="h6" sx={{ mt: 4, fontWeight: 'bold' }}>
              Ship To
            </Typography>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: '16px', width: '100%' }}>
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) =>
                  registerFieldRef?.('shipToAddress1', el)
                }
              >
                <FormInputTextField
                  control={control}
                  name="shipToAddress1"
                  label="Address 1"
                  required
                />
              </Box>
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) =>
                  registerFieldRef?.('shipToAddress2', el)
                }
              >
                <FormInputTextField
                  control={control}
                  name="shipToAddress2"
                  label="Address 2"
                />
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: '16px', width: '100%' }}>
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) =>
                  registerFieldRef?.('shipToPlace', el)
                }
              >
                <FormInputTextField
                  control={control}
                  name="shipToPlace"
                  label="Place"
                  required
                  fullWidth
                />
              </Box>
              <Box
                sx={{ flex: 1 }}
                ref={(el: HTMLDivElement | null) =>
                  registerFieldRef?.('shipToPincode', el)
                }
              >
                <FormInputTextField
                  control={control}
                  name="shipToPincode"
                  label="PinCode"
                  required
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              ref={(el: HTMLDivElement | null) =>
                registerFieldRef?.('shipToState', el)
              }
            >
              <FormInputAutosuggest
                control={control}
                name="shipToState"
                label="State"
                readOnly
                options={states.map((s) => ({
                  label: s.label,
                  id: s.code,
                }))}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export interface GenerateEInvoiceProps {
  onClose?: (result: string | null) => void
  eInvoiceId: number
  accountId?: number
}

export const GenerateEInvoice: FC<GenerateEInvoiceProps> = ({
  onClose,
  eInvoiceId,
  accountId,
}) => {
  const dispatchAction = useAppDispatch()
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const itemListSectionRef = useRef<HTMLDivElement>(null)

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [itemList, setItemList] = useState<EInvoiceItem[]>([])
  const [totals, setTotals] = useState<IrnValDetailsResponse | undefined>(
    undefined
  )
  const [reverseCharge, setReverseCharge] = useState(false)
  const [igstOnIntra, setIgstOnIntra] = useState(false)
  const [billFromAddress, setBillFromAddress] =
    useState<EInvoiceAddress | null>(null)
  const [billToAddress, setBillToAddress] = useState<EInvoiceAddress | null>(
    null
  )

  const form = useForm<Schema>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      supplyType: 'B2B',
      docType: 'INV',
      reverseCharge: false,
      dispatchFromState: states[0].code,
      shipToState: states[0].code,
    },
  })

  const { control, handleSubmit, setValue } = form

  useEffect(() => {
    if (!eInvoiceId) return
    setLoading(true)
    getEInvoiceInfo(eInvoiceId)
      .then((response) => {
        if (response.data?.success && response.data?.data) {
          const data: EInvoiceInfoResponse = response.data.data

          if (data.invoiceNumber) setValue('docNo', data.invoiceNumber)
          if (data.invoiceDate) {
            const parsed = new Date(data.invoiceDate)
            if (!isNaN(parsed.getTime())) setValue('docDate', parsed)
          }
          if (data.supplyType) setValue('supplyType', data.supplyType)
          if (data.docType) setValue('docType', data.docType)
          if (data.reverseCharge !== undefined) {
            setReverseCharge(data.reverseCharge)
            setValue('reverseCharge', data.reverseCharge)
          }
          if (data.igstOnIntra !== undefined) {
            setIgstOnIntra(data.igstOnIntra)
          }

          const bf = data.billFrom
          if (bf) {
            setValue('issuerName', bf.name ?? '')
            setValue('issuerGSTIN', bf.gstin ?? '')
            if (bf.address?.state)
              setValue('issuerState', addLeadingZero(bf.address.state))
            if (bf.address) setBillFromAddress(bf.address)
          }

          const bt = data.billTo
          if (bt) {
            setValue('partyName', bt.name ?? '')
            setValue('partyGSTIN', bt.gstin ?? '')
            if (bt.address?.state)
              setValue('partyState', addLeadingZero(bt.address.state))
            if (bt.address) setBillToAddress(bt.address)
          }

          const df = data.dispatchFrom
          if (df) {
            setValue('dispatchFromAddress1', df.address1 ?? '')
            setValue('dispatchFromAddress2', df.address2 ?? '')
            setValue('dispatchFromPlace', df.place ?? '')
            setValue(
              'dispatchFromPincode',
              df.pincode != null ? String(df.pincode) : ''
            )
            if (df.state)
              setValue('dispatchFromState', addLeadingZero(df.state))
          }

          if (data.shipToGstin) setValue('shipToGstin', data.shipToGstin)

          const st = data.shipTo
          if (st) {
            setValue('shipToAddress1', st.address1 ?? '')
            setValue('shipToAddress2', st.address2 ?? '')
            setValue('shipToPlace', st.place ?? '')
            setValue(
              'shipToPincode',
              st.pincode != null ? String(st.pincode) : ''
            )
            if (st.state) setValue('shipToState', addLeadingZero(st.state))
          }

          setItemList(
            (data.itemList ?? []).map((item) => ({
              ...item,
              isService: item.isService === 'Y',
              unitPrice: item.unitPrice ?? 0,
              amount: item.amount ?? 0,
              discount: item.discount ?? 0,
            }))
          )

          if (data.valDetails) setTotals(data.valDetails)
        } else {
          dispatchAction(
            showAlertAction({
              message:
                response.data?.message || 'Failed to load e-invoice details.',
              severity: 'error',
            })
          )
        }
      })
      .catch(
        (err: {
          response?: { data?: { message?: string } }
          message?: string
        }) => {
          dispatchAction(
            showAlertAction({
              message:
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load e-invoice details.',
              severity: 'error',
            })
          )
        }
      )
      .finally(() => setLoading(false))
  }, [eInvoiceId, dispatchAction])

  const validateItems = () => {
    if (itemList.length === 0) return false
    return itemList.every(
      (item) => item.hsnCode && item.quantity > 0 && item.taxableAmount > 0
    )
  }

  const scrollToFirstError = (errors: Record<string, unknown>) => {
    const key = Object.keys(errors)[0]
    if (!key) return
    fieldRefs.current[key]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }

  const submitHandle = handleSubmit(
    (data: Schema) => {
      if (!validateItems()) {
        dispatchAction(
          showAlertAction({
            message: 'Validation errors — check ITEM LIST section.',
            severity: 'error',
          })
        )
        itemListSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
        return
      }

      const dispatchFrom = {
        address1: data.dispatchFromAddress1,
        address2: data.dispatchFromAddress2 || null,
        place: data.dispatchFromPlace,
        pincode: data.dispatchFromPincode,
        state: data.dispatchFromState,
      }

      const shipTo = {
        address1: data.shipToAddress1,
        address2: data.shipToAddress2 || null,
        place: data.shipToPlace,
        pincode: data.shipToPincode,
        state: data.shipToState,
      }

      const request = {
        invoiceNumber: data.docNo,
        invoiceDate: getFormattedDate(data.docDate),
        docType: data.docType,
        supplyType: data.supplyType,
        reverseCharge: data.reverseCharge,
        igstOnIntra,
        shipToGstin: data.shipToGstin || null,
        billFrom: {
          name: data.issuerName,
          gstin: data.issuerGSTIN,
          address: billFromAddress,
        },
        billTo: {
          name: data.partyName,
          gstin: data.partyGSTIN,
          address: billToAddress,
        },
        dispatchFrom,
        shipTo,
        itemList: itemList.map((item) => ({
          ...item,
          isService: item.isService ? 'Y' : 'N',
        })),
        valDetails: {
          assVal: totals?.assVal ?? sumTaxableAmount(itemList),
          totInvVal: totals?.totInvVal ?? sumTotalValue(itemList),
          cgstVal: totals?.cgstVal ?? sumCgstValue(itemList),
          sgstVal: totals?.sgstVal ?? sumSgstValue(itemList),
          igstVal: totals?.igstVal ?? sumIgstValue(itemList),
        },
      }

      setSubmitting(true)
      generateEInvoice(eInvoiceId, request)
        .then((response) => {
          if (response.data?.success) {
            dispatchAction(
              showAlertAction({
                message: 'E-Invoice created successfully',
                severity: 'success',
              })
            )
            if (accountId) dispatchAction(fetchSlipsListAction(accountId))
            const pdfUrl = response.data?.data
            if (pdfUrl) window.open(pdfUrl, '_blank')
            onClose?.(pdfUrl ?? null)
          } else if (response.data?.message) {
            dispatchAction(
              showAlertAction({
                message: response.data.message,
                severity: 'error',
              })
            )
          }
        })
        .finally(() => setSubmitting(false))
    },
    (formErrors) => {
      setTimeout(() => scrollToFirstError(formErrors), 0)
    }
  )

  return (
    <AppDialog
      id="generate-einvoice"
      title="Generate E-Invoice"
      submitButtonText="Submit"
      onClose={() => onClose?.(null)}
      onSubmit={submitHandle}
      close={false}
      maxWidth="lg"
    >
      <FormProvider {...form}>
        <form>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            TRANSACTION DETAILS
          </Typography>
          <Grid container spacing={2} alignItems="center" wrap="nowrap">
            <Grid
              item
              sx={{ minWidth: 250 }}
              ref={(el: HTMLDivElement | null) => {
                fieldRefs.current['supplyType'] = el
              }}
            >
              <FormInputAutosuggest
                control={control}
                name="supplyType"
                label="Supply Type"
                readOnly
                options={supplyTypes.map((s) => ({ id: s.id, label: s.label }))}
              />
            </Grid>
            <Grid
              item
              sx={{ minWidth: 210 }}
              ref={(el: HTMLDivElement | null) => {
                fieldRefs.current['docType'] = el
              }}
            >
              <FormInputAutosuggest
                control={control}
                name="docType"
                label="Document Type"
                readOnly
                options={docTypes.map((d) => ({ id: d.id, label: d.label }))}
              />
            </Grid>
            <Grid
              item
              sx={{ minWidth: 210 }}
              ref={(el: HTMLDivElement | null) => {
                fieldRefs.current['docNo'] = el
              }}
            >
              <FormInputTextField
                control={control}
                name="docNo"
                label="Document No"
                required
                InputProps={{ readOnly: true, inputProps: { maxLength: 16 } }}
              />
            </Grid>
            <Grid
              item
              sx={{ minWidth: 150 }}
              ref={(el: HTMLDivElement | null) => {
                fieldRefs.current['docDate'] = el
              }}
            >
              <FormInputDateField
                format="dd/MM/yyyy"
                control={control}
                name="docDate"
                label="Document Date"
                disabled
              />
            </Grid>
            <Grid item sx={{ whiteSpace: 'nowrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={reverseCharge}
                    sx={{ pointerEvents: 'none' }}
                  />
                }
                label="Reverse Charge"
                componentsProps={{ typography: { fontWeight: 'bold' } }}
                sx={{ pointerEvents: 'none' }}
              />
            </Grid>
            <Grid item sx={{ whiteSpace: 'nowrap', ml: 'auto' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={igstOnIntra}
                    sx={{ pointerEvents: 'none' }}
                  />
                }
                label="IGST on Intra-state"
                componentsProps={{ typography: { fontWeight: 'bold' } }}
                sx={{ pointerEvents: 'none' }}
              />
            </Grid>
          </Grid>

          <IssuerDispatchBox
            registerFieldRef={(name, el) => {
              fieldRefs.current[name] = el
            }}
          />
          <PartyShipToBox
            registerFieldRef={(name, el) => {
              fieldRefs.current[name] = el
            }}
          />

          <Box ref={itemListSectionRef}>
            <ItemListBox
              itemList={itemList}
              setItemList={setItemList}
              totals={totals}
            />
          </Box>
        </form>
      </FormProvider>
      {(loading || submitting) && <Loader />}
    </AppDialog>
  )
}
